import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import {
  MANAGER_DECISION,
  MANAGER_REJECT_REASONS,
} from '../../helper/odulOneriManagerReasons'
import {
  BENEFIT_KINDS,
  IMPLEMENTATION_METHODS,
  amountInputToNumberOrNull,
} from '../../helper/odulOneriImplementation'
import MoneyInput from './MoneyInput'
import './YoneticiDegerlendirme.css'

const isoToday = () =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())

const managerTokenErrorMessage = (response) => {
  switch (response?.error) {
    case 'already_used':
      return 'Bu değerlendirme bağlantısı daha önce kullanılmış.'
    case 'already_decided':
      return `Bu kayıt için yönetici kararı zaten verilmiş${
        response.recordNumber ? ` (${response.recordNumber})` : ''
      }.`
    case 'not_found':
    case 'missing':
      return 'Bağlantı geçersiz.'
    case 'wrong_role':
      return 'Bu bağlantı yönetici değerlendirmesi için değil.'
    case 'record_missing':
      return 'Kayıt bulunamadı.'
    case 'load_failed':
      return 'Yükleme hatası. Sayfayı yenileyin.'
    default:
      return 'Geçersiz veya süresi dolmuş bağlantı.'
  }
}

const emptyPlan = {
  ownerName: '',
  leaderName: '',
  leaderEmail: '',
  team: '',
  startDate: '',
  dueDate: '',
  workSummary: '',
  managerNote: '',
}

const emptyBenefit = {
  kind: 'measurable',
  type: '',
  baseline: '',
  target: '',
  unit: '',
  kpi: '',
  measureSource: '',
  estimatedCost: '',
  estimatedGain: '',
  qualitativeNote: '',
}

const YoneticiDegerlendirme = () => {
  const { token } = useParams()
  const { getManagerEvaluationByToken, submitManagerDecision, fetchDepartments } =
    useOdulOneriCall()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [record, setRecord] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [clientError, setClientError] = useState('')

  const [result, setResult] = useState('')
  const [reasons, setReasons] = useState([])
  const [justification, setJustification] = useState('')
  const [evaluatorName, setEvaluatorName] = useState('')
  const [method, setMethod] = useState('quick')
  const [plan, setPlan] = useState(emptyPlan)
  const [benefit, setBenefit] = useState(emptyBenefit)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      setLoading(true)
      const response = await getManagerEvaluationByToken(token)
      if (!mounted) return
      if (!response.ok) {
        setLoadError(managerTokenErrorMessage(response))
        setRecord(null)
      } else {
        setLoadError('')
        setRecord(response.record)
        const departments = await fetchDepartments()
        if (!mounted) return
        const dept =
          departments.find((d) => d.id === response.record?.relatedDepartmentId) || null
        setEvaluatorName(
          String(dept?.managerName || response.record?.assignee?.name || '').trim()
        )
        setPlan((p) => ({
          ...p,
          ownerName: response.record?.applicant?.fullName || '',
          startDate: p.startDate || isoToday(),
        }))
      }
      setLoading(false)
    })()
    return () => {
      mounted = false
    }
  }, [token, getManagerEvaluationByToken, fetchDepartments])

  const toggleReason = (reason) => {
    setReasons((prev) =>
      prev.includes(reason) ? prev.filter((x) => x !== reason) : [...prev, reason]
    )
  }

  const validate = () => {
    if (!result) return 'Kabul veya red seçiniz.'
    if (!evaluatorName.trim()) return 'Değerlendiren adı zorunludur.'
    if (result === MANAGER_DECISION.REJECT) {
      if (!reasons.length) return 'En az bir red nedeni seçiniz.'
      if (!justification.trim()) return 'Gerekçe zorunludur.'
    }
    if (result === MANAGER_DECISION.ACCEPT) {
      if (!plan.leaderName.trim()) return 'Uygulama lideri adı zorunludur.'
      if (!plan.leaderEmail.trim().includes('@')) return 'Uygulama lideri e-postası zorunludur.'
      if (!plan.startDate) return 'Başlangıç tarihi zorunludur.'
      if (!plan.dueDate) return 'Termin tarihi zorunludur.'
      if (plan.dueDate < plan.startDate) {
        return 'Hedef bitiş tarihi başlangıçtan önce olamaz.'
      }
      if (!plan.workSummary.trim()) return 'İş özeti zorunludur.'
      if (benefit.kind === 'measurable' && !benefit.kpi.trim()) {
        return 'Ölçülebilir fayda için KPI alanı zorunludur.'
      }
      if (benefit.kind === 'qualitative' && !benefit.qualitativeNote.trim()) {
        return 'Nitel fayda açıklaması zorunludur.'
      }
    }
    return ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const err = validate()
    if (err) {
      setClientError(err)
      return
    }
    setClientError('')
    setSubmitting(true)
    try {
      const payload =
        result === MANAGER_DECISION.REJECT
          ? {
              result: MANAGER_DECISION.REJECT,
              reasons,
              justification: justification.trim(),
              evaluatorName: evaluatorName.trim(),
            }
          : {
              result: MANAGER_DECISION.ACCEPT,
              method,
              evaluatorName: evaluatorName.trim(),
              plan: {
                ...plan,
                leaderEmail: plan.leaderEmail.trim().toLowerCase(),
              },
              benefit: {
                ...benefit,
                estimatedCost: amountInputToNumberOrNull(benefit.estimatedCost),
                estimatedGain: amountInputToNumberOrNull(benefit.estimatedGain),
              },
            }
      await submitManagerDecision(token, payload)
      setSubmitted(true)
    } catch (_) {
      /* toast */
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="odul-yonetici">
        <div className="loading">Yükleniyor…</div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="odul-yonetici">
        <div className="error-box">{loadError}</div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="odul-yonetici">
        <div className="success-box">
          Değerlendirme kaydedildi. Kayıt No: {record?.recordNumber}
        </div>
      </div>
    )
  }

  const applicant = record?.applicant || {}
  const attachments = record?.currentStateAttachments || []

  return (
    <div className="odul-yonetici">
      <header className="odul-yonetici-header">
        <div>
          <div className="eyebrow">BONNA • ÖNERİ UYGULAMA MODÜLÜ</div>
          <h1>Öneri Değerlendirme ve Uygulama Tanımı</h1>
        </div>
        <span className="badge">BÖLÜM YÖNETİCİSİ KARARI BEKLENİYOR</span>
      </header>

      <div className="page">
        <div className="notice">
          Sayın {evaluatorName || 'Yönetici'}, {record?.recordNumber} numaralı öneri için kabul
          veya ret kararınızı veriniz. Kabul etmeniz halinde uygulama planını tanımlayınız.
        </div>

        <div className="statusbar" aria-hidden="true">
          <div className="step done" />
          <div className="step active" />
          <div className="step" />
          <div className="step" />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid">
            <section className="card wide">
              <h2>
                <span className="num">0</span> Gelen Öneri Detayları — {record?.recordNumber}
              </h2>
              <div className="facts">
                <div className="fact">
                  <span>Öneri sahibi</span>
                  <strong>
                    {applicant.fullName || '—'}
                    {applicant.role ? ` (${applicant.role})` : ''}
                  </strong>
                </div>
                <div className="fact">
                  <span>Bölüm / Birim</span>
                  <strong>{applicant.employeeDept || '—'}</strong>
                </div>
                <div className="fact">
                  <span>İlgili bölüm</span>
                  <strong>{record?.relatedDepartmentName || '—'}</strong>
                </div>
                <div className="fact">
                  <span>Lokasyon</span>
                  <strong>{record?.location || record?.facility || '—'}</strong>
                </div>
              </div>
              <div className="fact" style={{ marginTop: 8 }}>
                <span>Öneri konusu</span>
                <strong>{record?.topic || '—'}</strong>
              </div>
              <div>
                <span className="field-title">Mevcut durum</span>
                <div className="copy">{record?.currentState || '—'}</div>
              </div>
              <div>
                <span className="field-title">Önerilen durum</span>
                <div className="copy">{record?.proposedState || '—'}</div>
              </div>
              {attachments.length > 0 && (
                <div className="attach" style={{ marginTop: 10 }}>
                  <span className="field-title">Ekler</span>
                  {attachments.map((a) => (
                    <a key={a.id || a.url} href={a.url} target="_blank" rel="noopener noreferrer">
                      {a.name || 'Ek'}
                    </a>
                  ))}
                </div>
              )}
            </section>

            <section className="card wide">
              <h2>
                <span className="num">1</span> Bölüm Yöneticisi Kararı
              </h2>
              <div className="decision-row">
                <button
                  type="button"
                  className={`decision-opt${
                    result === MANAGER_DECISION.ACCEPT ? ' selected' : ''
                  }`}
                  onClick={() => {
                    setResult(MANAGER_DECISION.ACCEPT)
                    setPlan((p) => ({ ...p, startDate: p.startDate || isoToday() }))
                    setClientError('')
                  }}
                >
                  Öneriyi Kabul Et
                </button>
                <button
                  type="button"
                  className={`decision-opt reject${
                    result === MANAGER_DECISION.REJECT ? ' selected' : ''
                  }`}
                  onClick={() => {
                    setResult(MANAGER_DECISION.REJECT)
                    setClientError('')
                  }}
                >
                  Öneriyi Reddet
                </button>
              </div>
            </section>

            {result === MANAGER_DECISION.REJECT && (
              <section className="card wide">
                <h2>
                  <span className="num">2</span> Ret Nedeni
                </h2>
                <div className="check-grid">
                  {MANAGER_REJECT_REASONS.map((r) => (
                    <label key={r}>
                      <input
                        type="checkbox"
                        checked={reasons.includes(r)}
                        onChange={() => toggleReason(r)}
                      />
                      {r}
                    </label>
                  ))}
                </div>
                <label className="full" style={{ display: 'block', marginTop: 12 }}>
                  <span>Ret açıklaması</span>
                  <textarea
                    required
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    placeholder="Gerekçeyi yazınız…"
                  />
                </label>
              </section>
            )}

            {result === MANAGER_DECISION.ACCEPT && (
              <>
                <section className="card wide">
                  <h2>
                    <span className="num">2</span> Uygulama Yöntemi
                  </h2>
                  <div className="types">
                    {IMPLEMENTATION_METHODS.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        className={`type${method === m.value ? ' selected' : ''}`}
                        onClick={() => setMethod(m.value)}
                      >
                        <strong>{m.label}</strong>
                        <small>{m.hint}</small>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="card wide">
                  <h2>
                    <span className="num">3</span> Sorumluluklar, Ana Termin ve Uygulama Özeti
                  </h2>
                  <div className="fields">
                    <label>
                      <span>Uygulama sahibi</span>
                      <input
                        value={plan.ownerName}
                        onChange={(e) => setPlan((p) => ({ ...p, ownerName: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Uygulama lideri *</span>
                      <input
                        required
                        value={plan.leaderName}
                        onChange={(e) => setPlan((p) => ({ ...p, leaderName: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Lider e-posta *</span>
                      <input
                        required
                        type="email"
                        value={plan.leaderEmail}
                        onChange={(e) => setPlan((p) => ({ ...p, leaderEmail: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Ekip üyeleri</span>
                      <input
                        value={plan.team}
                        onChange={(e) => setPlan((p) => ({ ...p, team: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Başlangıç tarihi *</span>
                      <input
                        required
                        type="date"
                        value={plan.startDate}
                        onChange={(e) => setPlan((p) => ({ ...p, startDate: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Hedef bitiş tarihi *</span>
                      <input
                        required
                        type="date"
                        value={plan.dueDate}
                        onChange={(e) => setPlan((p) => ({ ...p, dueDate: e.target.value }))}
                      />
                    </label>
                    <label className="full">
                      <span>Yapılacak çalışma / aksiyon *</span>
                      <textarea
                        required
                        value={plan.workSummary}
                        onChange={(e) => setPlan((p) => ({ ...p, workSummary: e.target.value }))}
                      />
                    </label>
                  </div>
                </section>

                <section className="card wide">
                  <h2>
                    <span className="num">4</span> Beklenen Fayda ve Başarı Ölçümü
                  </h2>
                  <div className="benefit-switch">
                    {BENEFIT_KINDS.map((k) => (
                      <button
                        key={k.value}
                        type="button"
                        className={benefit.kind === k.value ? 'selected' : ''}
                        onClick={() => setBenefit((b) => ({ ...b, kind: k.value }))}
                      >
                        {k.label === 'Ölçülebilir'
                          ? 'Sayısal olarak ölçülebilir'
                          : 'Nitel / parasal olmayan'}
                      </button>
                    ))}
                  </div>
                  {benefit.kind === 'measurable' ? (
                    <div className="fields">
                      <label className="full">
                        <span>Başarı KPI’ı *</span>
                        <input
                          value={benefit.kpi}
                          onChange={(e) => setBenefit((b) => ({ ...b, kpi: e.target.value }))}
                        />
                      </label>
                      <label>
                        <span>Mevcut değer</span>
                        <input
                          value={benefit.baseline}
                          onChange={(e) =>
                            setBenefit((b) => ({ ...b, baseline: e.target.value }))
                          }
                        />
                      </label>
                      <label>
                        <span>Hedef değer</span>
                        <input
                          value={benefit.target}
                          onChange={(e) => setBenefit((b) => ({ ...b, target: e.target.value }))}
                        />
                      </label>
                      <label>
                        <span>Ölçü birimi</span>
                        <input
                          value={benefit.unit}
                          onChange={(e) => setBenefit((b) => ({ ...b, unit: e.target.value }))}
                        />
                      </label>
                      <label>
                        <span>Ölçüm yöntemi / veri kaynağı</span>
                        <input
                          value={benefit.measureSource}
                          onChange={(e) =>
                            setBenefit((b) => ({ ...b, measureSource: e.target.value }))
                          }
                        />
                      </label>
                      <label>
                        <span>Tahmini uygulama maliyeti (€)</span>
                        <MoneyInput
                          value={benefit.estimatedCost}
                          onChange={(v) => setBenefit((b) => ({ ...b, estimatedCost: v }))}
                        />
                      </label>
                      <label>
                        <span>Tahmini yıllık kazanç (€)</span>
                        <MoneyInput
                          value={benefit.estimatedGain}
                          onChange={(v) => setBenefit((b) => ({ ...b, estimatedGain: v }))}
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="full" style={{ display: 'block' }}>
                      <span>Nitel fayda / başarı kanıtı *</span>
                      <textarea
                        required
                        value={benefit.qualitativeNote}
                        onChange={(e) =>
                          setBenefit((b) => ({ ...b, qualitativeNote: e.target.value }))
                        }
                      />
                    </label>
                  )}
                </section>

                <section className="card wide">
                  <h2>
                    <span className="num">5</span> Yönetici Notu
                  </h2>
                  <label className="full" style={{ display: 'block' }}>
                    <span>Not (opsiyonel)</span>
                    <textarea
                      value={plan.managerNote}
                      onChange={(e) => setPlan((p) => ({ ...p, managerNote: e.target.value }))}
                    />
                  </label>
                </section>
              </>
            )}

            <section className="card wide">
              <h2>
                <span className="num">✓</span> Değerlendiren
              </h2>
              <label style={{ display: 'block' }}>
                <span>Ad soyad *</span>
                <input
                  required
                  value={evaluatorName}
                  onChange={(e) => setEvaluatorName(e.target.value)}
                />
              </label>
              {clientError && <div className="error">{clientError}</div>}
            </section>
          </div>

          <div className="footer-bar">
            <div className="hint">
              {result
                ? 'Kararınızı gözden geçirip kaydediniz.'
                : 'Kararınızı seçiniz.'}
            </div>
            <button className="btn-primary" type="submit" disabled={submitting || !result}>
              {submitting ? 'Kaydediliyor…' : 'Kararı Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default YoneticiDegerlendirme
